import { createColumnHelper } from '@tanstack/react-table';
import { WithAccountId } from '@mews-commander/types';
import { LocalizedDate, LocalizedText } from '@mews/framework';
import { Dash, MultilineText, TooltippedTag } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { parseExpirationDate } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { PaymentMethodStateTag } from '../../common/PaymentMethodStateTag';
import { CreatedCell } from '../Payments/CreatedCell';
import { PaymentMethodsActionsMenu } from './PaymentMethodsActionsMenu';
import { PaymentMethodRecord } from './types';
import { isPaymentCardPaymentMethod } from './utils';
import { PaymentMethodSidebarLink } from './PaymentMethodSidebarLink';
import { PaymentMethodType } from './PaymentMethodType';

const columnHelper = createColumnHelper<PaymentMethodRecord>();

export const getPaymentMethodsColumns = ({ accountId, timezone }: { timezone: string } & WithAccountId) => [
    columnHelper.accessor('title', {
        header: () => <LocalizedText textKey={TextKey.Method} />,
        cell: ({
            row: {
                original: { lastDigits, id, title, paymentMethod },
            },
        }) =>
            lastDigits && (
                <PaymentMethodSidebarLink
                    paymentMethodId={id}
                    paymentMethod={paymentMethod}
                    sidebarTitle={title}
                    linkTitle={title}
                />
            ),
    }),
    columnHelper.accessor('format', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: ({
            row: {
                original: { paymentMethod, wallet, format, paymentPlanId },
            },
        }) => (
            <PaymentMethodType
                paymentMethod={paymentMethod}
                wallet={wallet}
                format={format}
                paymentPlanId={paymentPlanId}
            />
        ),
    }),
    columnHelper.accessor('holderName', {
        header: () => <LocalizedText textKey={TextKey.Owner} />,

        cell: ({
            row: {
                original: { payerName, paymentMethod, holderName },
            },
        }) => (isPaymentCardPaymentMethod(paymentMethod) ? holderName : payerName),
    }),
    columnHelper.accessor('storageId', {
        header: () => <LocalizedText textKey={TextKey.Identifier} />,
        cell: ({
            row: {
                original: { storageId },
            },
        }) =>
            storageId ? (
                <TooltippedTag variant="basicSubtle" tooltip={storageId}>
                    <LocalizedText textKey={TextKey.PaymentCardTokenized} />
                </TooltippedTag>
            ) : (
                <Dash />
            ),
    }),
    columnHelper.accessor('expiration', {
        header: () => <LocalizedText textKey={TextKey.Expiration} />,
        cell: ({
            row: {
                original: { expiration },
            },
        }) => (expiration ? <LocalizedDate date={parseExpirationDate(expiration, timezone)} /> : <Dash />),
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.LatestCapture} />,
        cell: ({
            row: {
                original: { createdUtc, creatorProfileName },
            },
        }) => <CreatedCell createdUtc={createdUtc} creatorProfileName={creatorProfileName} />,
    }),
    columnHelper.accessor('source', {
        header: () => <LocalizedText textKey={TextKey.LatestSource} />,
        cell: ({
            row: {
                original: { source },
            },
        }) => source && <MultilineText text={source} />,
    }),
    columnHelper.accessor('state', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { state, paymentMethod },
            },
        }) => <PaymentMethodStateTag paymentMethod={paymentMethod} state={state} />,
    }),
    columnHelper.accessor('paymentMethodActions', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { lastDigits, id, title, isGatewayProvider },
            },
            getValue,
        }) =>
            !!accountId &&
            lastDigits && (
                <PaymentMethodsActionsMenu
                    paymentMethodActions={getValue()}
                    paymentMethodId={id}
                    accountId={accountId}
                    paymentMethodTitle={title}
                    isGatewayPaymentMethod={isGatewayProvider}
                />
            ),
    }),
];
